/**
 * Admin Appearance / Running Banner / Platform Fee editor.
 * Renders inside AdminSettings. Uses "form-local" state so changes are LIVE-PREVIEWED
 * inside this panel only. They apply site-wide ONLY after the parent's Save button
 * calls PUT /api/settings and refreshes SettingsContext.
 */
import { useMemo, useRef } from 'react';
import { toast } from 'sonner';
import { DEFAULT_APPEARANCE, DEFAULT_RUNNING_BANNER } from '@/context/SettingsContext';

const SHADOW_OPTS = ['none', 'soft', 'medium', 'strong'];
const SIZE_OPTS = ['small', 'medium', 'large'];
const SPEED_OPTS = ['slow', 'normal', 'fast'];
const THEME_OPTS = ['light', 'dark'];
const WA_POS_OPTS = ['bottom-right', 'bottom-left'];
const FONT_OPTS = [
  'Figtree', 'Inter', 'Roboto', 'Poppins', 'Nunito', 'Manrope',
  'Plus Jakarta Sans', 'DM Sans', 'Work Sans', 'Lato', 'Open Sans',
  'Montserrat', 'Fraunces', 'Playfair Display', 'system-ui',
];
const BANNER_STYLE_OPTS = ['marquee', 'static'];

function Row({ label, children, hint }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-stone-600 block">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-stone-500">{hint}</p>}
    </div>
  );
}

function Color({ value, onChange, testid }) {
  return (
    <div className="flex items-center gap-2">
      <input data-testid={testid} type="color" value={value || '#000000'} onChange={(e) => onChange(e.target.value)} className="h-10 w-14 rounded-lg border border-stone-200 cursor-pointer" />
      <input value={value || ''} onChange={(e) => onChange(e.target.value)} className="flex-1 h-10 px-3 rounded-lg bg-white border border-stone-200 text-sm font-mono" />
    </div>
  );
}

function Num({ value, onChange, min = 0, max = 200, step = 1, testid }) {
  return (
    <div className="flex items-center gap-2">
      <input type="range" min={min} max={max} step={step} value={Number(value) || 0} onChange={(e) => onChange(Number(e.target.value))} className="flex-1 accent-fresh-600" />
      <input data-testid={testid} type="number" value={Number(value) || 0} onChange={(e) => onChange(Number(e.target.value))} className="w-20 h-10 px-2 rounded-lg bg-white border border-stone-200 text-sm text-right" />
    </div>
  );
}

function Sel({ value, onChange, options, testid }) {
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-10 px-3 rounded-lg bg-white border border-stone-200 text-sm">
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function Toggle({ value, onChange, testid, label }) {
  return (
    <label className="flex items-center gap-2 text-sm select-none">
      <input data-testid={testid} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-fresh-600" />
      {label}
    </label>
  );
}

/** Live preview surface (isolated - style vars only apply inside this scoped div) */
function LivePreview({ a }) {
  const scoped = useMemo(() => ({
    '--pv-bg': a.background_color,
    '--pv-surface': a.surface_color,
    '--pv-text': a.text_color,
    '--pv-primary': a.primary_color,
    '--pv-primary-hover': a.primary_hover_color,
    '--pv-accent': a.accent_color,
    '--pv-btn-text': a.button_text_color,
    '--pv-btn-radius': `${a.button_radius}px`,
    '--pv-card-radius': `${a.card_radius}px`,
    '--pv-product-radius': `${a.product_image_radius}px`,
    '--pv-border': a.border_color,
    '--pv-badge': a.badge_color,
    '--pv-search-bg': a.search_bg,
    '--pv-search-radius': `${a.search_radius}px`,
    fontFamily: `'${a.font_family}', system-ui, sans-serif`,
    fontSize: `${a.font_size_base}px`,
  }), [a]);
  const btnStyle = { background: 'var(--pv-primary)', color: 'var(--pv-btn-text)', borderRadius: 'var(--pv-btn-radius)' };
  const cardStyle = { background: 'var(--pv-surface)', borderColor: 'var(--pv-border)', borderRadius: 'var(--pv-card-radius)' };
  return (
    <div data-testid="appearance-live-preview" className="rounded-3xl overflow-hidden border border-stone-200 shadow-sm" style={scoped}>
      <div className="px-4 py-3 text-xs" style={{ background: a.footer_bg, color: a.footer_text_color }}>Header preview</div>
      <div style={{ background: 'var(--pv-bg)', color: 'var(--pv-text)' }} className="p-5 space-y-4">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-2xl grid place-items-center font-heading font-bold" style={{ background: 'var(--pv-primary)', color: 'var(--pv-btn-text)' }}>MZK</span>
          <div>
            <p className="font-heading font-bold text-lg">Live theme preview</p>
            <p className="text-xs opacity-70">Changes apply to the site only after you Save.</p>
          </div>
        </div>
        <div className="flex items-center h-10 px-3 border" style={{ background: 'var(--pv-search-bg)', borderColor: a.search_border, borderRadius: 'var(--pv-search-radius)', color: a.search_placeholder_color }}>
          <span className="text-sm">Search products…</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 border" style={cardStyle}>
            <div className="h-20 rounded-xl mb-2" style={{ background: '#DDE6D2', borderRadius: 'var(--pv-product-radius)' }} />
            <p className="text-sm font-semibold">Fresh Apples</p>
            <p className="text-xs opacity-70">1 kg</p>
            <button className="mt-2 h-8 px-4 text-xs font-semibold" style={btnStyle}>ADD</button>
          </div>
          <div className="p-3 border" style={cardStyle}>
            <div className="h-20 rounded-xl mb-2" style={{ background: '#F4E7D8', borderRadius: 'var(--pv-product-radius)' }} />
            <p className="text-sm font-semibold">Basmati Rice</p>
            <p className="text-xs opacity-70">5 kg · <span className="px-1.5 py-0.5 rounded text-white text-[10px]" style={{ background: 'var(--pv-badge)' }}>NEW</span></p>
            <button className="mt-2 h-8 px-4 text-xs font-semibold" style={btnStyle}>ADD</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AppearancePanel({ form, setForm }) {
  const a = form.appearance || DEFAULT_APPEARANCE;
  const rb = form.running_banner || DEFAULT_RUNNING_BANNER;
  const fileRef = useRef(null);

  const setA = (k) => (v) => setForm({ ...form, appearance: { ...a, [k]: v } });
  const setRB = (k) => (v) => setForm({ ...form, running_banner: { ...rb, [k]: v } });

  const resetDefaults = () => {
    setForm({ ...form, appearance: { ...DEFAULT_APPEARANCE }, running_banner: { ...DEFAULT_RUNNING_BANNER } });
    toast.success('Restored default theme (click Save to apply)');
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify({ appearance: a, running_banner: rb }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `mzk-appearance-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const importJson = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(reader.result);
        const nextA = { ...DEFAULT_APPEARANCE, ...(data.appearance || data) };
        const nextRB = { ...DEFAULT_RUNNING_BANNER, ...(data.running_banner || {}) };
        setForm({ ...form, appearance: nextA, running_banner: nextRB });
        toast.success('Appearance imported (click Save to apply)');
      } catch (err) {
        toast.error('Invalid appearance JSON');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {/* LEFT: form fields */}
      <div className="lg:col-span-2 space-y-6">
        <SectionCard title="Platform Fee & Store" testid="section-platform-store">
          <div className="grid sm:grid-cols-2 gap-4">
            <Row label="Platform Fee (₹) · Set 0 to disable" hint="Applied automatically at checkout. Any value ≥ 0 accepted (0, 2, 5, 6.99, 10 …).">
              <input
                data-testid="admin-settings-platform-fee"
                type="number" min="0" step="0.01"
                value={form.platform_fee ?? 0}
                onChange={(e) => setForm({ ...form, platform_fee: Number(e.target.value) })}
                className="w-full h-10 px-3 rounded-lg bg-white border border-stone-200 text-sm"
              />
            </Row>
            <Row label="Store Contact Number (visible on site)">
              <input
                data-testid="admin-settings-store-number"
                value={form.store_phone || ''}
                onChange={(e) => setForm({ ...form, store_phone: e.target.value })}
                className="w-full h-10 px-3 rounded-lg bg-white border border-stone-200 text-sm"
                placeholder="+91 9971236105"
              />
            </Row>
          </div>
        </SectionCard>

        <SectionCard title="Running Banner" testid="section-running-banner">
          <div className="grid sm:grid-cols-2 gap-4">
            <Toggle testid="admin-rb-enabled" value={rb.enabled} onChange={setRB('enabled')} label="Enable running banner" />
            <Row label="Style"><Sel testid="admin-rb-style" value={rb.style} onChange={setRB('style')} options={BANNER_STYLE_OPTS} /></Row>
            <Row label="Text" hint="Displayed as scrolling ticker or static bar">
              <input data-testid="admin-rb-text" value={rb.text || ''} onChange={(e) => setRB('text')(e.target.value)} className="w-full h-10 px-3 rounded-lg bg-white border border-stone-200 text-sm" />
            </Row>
            <Row label="Auto scroll speed (seconds/loop)"><Num testid="admin-rb-speed" value={rb.speed} onChange={setRB('speed')} min={5} max={120} /></Row>
            <Row label="Background color"><Color testid="admin-rb-bg" value={rb.bg_color} onChange={setRB('bg_color')} /></Row>
            <Row label="Text color"><Color testid="admin-rb-fg" value={rb.text_color} onChange={setRB('text_color')} /></Row>
            <Toggle testid="admin-rb-dismissible" value={rb.dismissible} onChange={setRB('dismissible')} label="Allow visitor to dismiss (static style only)" />
          </div>
        </SectionCard>

        <SectionCard title="Typography" testid="section-typography">
          <div className="grid sm:grid-cols-2 gap-4">
            <Row label="Font family"><Sel testid="admin-app-font-family" value={a.font_family} onChange={setA('font_family')} options={FONT_OPTS} /></Row>
            <Row label="Global font size (px)"><Num testid="admin-app-font-size" value={a.font_size_base} onChange={setA('font_size_base')} min={12} max={22} /></Row>
          </div>
        </SectionCard>

        <SectionCard title="Colors" testid="section-colors">
          <div className="grid sm:grid-cols-2 gap-4">
            <Row label="Site background"><Color testid="admin-app-bg" value={a.background_color} onChange={setA('background_color')} /></Row>
            <Row label="Card / surface"><Color testid="admin-app-surface" value={a.surface_color} onChange={setA('surface_color')} /></Row>
            <Row label="Text color"><Color testid="admin-app-text" value={a.text_color} onChange={setA('text_color')} /></Row>
            <Row label="Muted text"><Color value={a.muted_text_color} onChange={setA('muted_text_color')} /></Row>
            <Row label="Primary theme color"><Color testid="admin-app-primary" value={a.primary_color} onChange={setA('primary_color')} /></Row>
            <Row label="Primary hover"><Color value={a.primary_hover_color} onChange={setA('primary_hover_color')} /></Row>
            <Row label="Accent / secondary"><Color testid="admin-app-accent" value={a.accent_color} onChange={setA('accent_color')} /></Row>
            <Row label="Button text color"><Color value={a.button_text_color} onChange={setA('button_text_color')} /></Row>
            <Row label="Button hover color"><Color value={a.button_hover_color} onChange={setA('button_hover_color')} /></Row>
            <Row label="Badge color"><Color value={a.badge_color} onChange={setA('badge_color')} /></Row>
            <Row label="Border color"><Color value={a.border_color} onChange={setA('border_color')} /></Row>
          </div>
        </SectionCard>

        <SectionCard title="Shape · Radius · Shadow" testid="section-shape">
          <div className="grid sm:grid-cols-2 gap-4">
            <Row label="Button border radius (px)"><Num testid="admin-app-btn-radius" value={a.button_radius} onChange={setA('button_radius')} min={0} max={40} /></Row>
            <Row label="Card border radius (px)"><Num value={a.card_radius} onChange={setA('card_radius')} min={0} max={40} /></Row>
            <Row label="Product image corner radius (px)"><Num value={a.product_image_radius} onChange={setA('product_image_radius')} min={0} max={40} /></Row>
            <Row label="Card shadow"><Sel value={a.card_shadow} onChange={setA('card_shadow')} options={SHADOW_OPTS} /></Row>
            <Row label="Product image size"><Sel value={a.product_image_size} onChange={setA('product_image_size')} options={SIZE_OPTS} /></Row>
            <Row label="Card grid gap (px)"><Num value={a.grid_gap} onChange={setA('grid_gap')} min={0} max={48} /></Row>
            <Row label="Section padding (px)"><Num value={a.section_padding} onChange={setA('section_padding')} min={0} max={80} /></Row>
          </div>
        </SectionCard>

        <SectionCard title="Header · Search · Navigation" testid="section-header">
          <div className="grid sm:grid-cols-2 gap-4">
            <Row label="Header height (px)"><Num value={a.header_height} onChange={setA('header_height')} min={48} max={96} /></Row>
            <Toggle value={a.sticky_header} onChange={setA('sticky_header')} label="Sticky header (stays on scroll)" />
            <Row label="Navbar background"><Color value={a.navbar_bg} onChange={setA('navbar_bg')} /></Row>
            <Row label="Navbar icon color"><Color value={a.navbar_icon_color} onChange={setA('navbar_icon_color')} /></Row>
            <Row label="Bottom nav background"><Color value={a.bottom_nav_bg} onChange={setA('bottom_nav_bg')} /></Row>
            <Row label="Bottom nav icon color"><Color value={a.bottom_nav_icon_color} onChange={setA('bottom_nav_icon_color')} /></Row>
            <Row label="Search bar background"><Color value={a.search_bg} onChange={setA('search_bg')} /></Row>
            <Row label="Search bar border"><Color value={a.search_border} onChange={setA('search_border')} /></Row>
            <Row label="Search bar radius (px)"><Num value={a.search_radius} onChange={setA('search_radius')} min={0} max={999} /></Row>
            <Row label="Search placeholder color"><Color value={a.search_placeholder_color} onChange={setA('search_placeholder_color')} /></Row>
          </div>
        </SectionCard>

        <SectionCard title="Footer · WhatsApp · Banner" testid="section-footer">
          <div className="grid sm:grid-cols-2 gap-4">
            <Row label="Footer background"><Color value={a.footer_bg} onChange={setA('footer_bg')} /></Row>
            <Row label="Footer text color"><Color value={a.footer_text_color} onChange={setA('footer_text_color')} /></Row>
            <Row label="WhatsApp button color"><Color value={a.whatsapp_button_color} onChange={setA('whatsapp_button_color')} /></Row>
            <Row label="WhatsApp position"><Sel value={a.whatsapp_position} onChange={setA('whatsapp_position')} options={WA_POS_OPTS} /></Row>
            <Row label="Running banner height (px)"><Num value={a.banner_height} onChange={setA('banner_height')} min={28} max={80} /></Row>
          </div>
        </SectionCard>

        <SectionCard title="Motion · Theme mode" testid="section-motion">
          <div className="grid sm:grid-cols-2 gap-4">
            <Toggle testid="admin-app-anim-on" value={a.animations_enabled} onChange={setA('animations_enabled')} label="Enable global animations" />
            <Row label="Animation speed"><Sel value={a.animation_speed} onChange={setA('animation_speed')} options={SPEED_OPTS} /></Row>
            <Row label="Theme mode"><Sel testid="admin-app-theme-mode" value={a.theme_mode} onChange={setA('theme_mode')} options={THEME_OPTS} /></Row>
          </div>
        </SectionCard>

        <div className="flex flex-wrap gap-3 pt-2">
          <button type="button" data-testid="admin-app-reset" onClick={resetDefaults} className="h-10 px-4 rounded-full border border-stone-300 text-sm font-medium hover:bg-stone-50">Restore defaults</button>
          <button type="button" data-testid="admin-app-export" onClick={exportJson} className="h-10 px-4 rounded-full border border-stone-300 text-sm font-medium hover:bg-stone-50">Export appearance</button>
          <button type="button" data-testid="admin-app-import-btn" onClick={() => fileRef.current?.click()} className="h-10 px-4 rounded-full border border-stone-300 text-sm font-medium hover:bg-stone-50">Import appearance</button>
          <input ref={fileRef} type="file" accept="application/json" onChange={importJson} className="hidden" data-testid="admin-app-import-file" />
        </div>
      </div>

      {/* RIGHT: live preview (sticky) */}
      <aside className="lg:sticky lg:top-24 self-start space-y-3">
        <p className="text-xs font-medium text-stone-500 uppercase tracking-wide">Live preview (admin-only)</p>
        <LivePreview a={a} />
        <p className="text-[11px] text-stone-500 leading-snug">Changes below are local to this panel. They apply to the entire website only after you click <b>Save Settings</b>.</p>
      </aside>
    </div>
  );
}

function SectionCard({ title, children, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-stone-200 rounded-3xl p-5 sm:p-6 space-y-4">
      <h2 className="font-heading text-lg text-stone-900">{title}</h2>
      {children}
    </div>
  );
}
